/* eslint-disable */
import { CheckPicker } from 'rsuite';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState, useRef } from 'react';
import { ClipboardPaste, X } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { useMultiplePo } from './contexts/MultiplePoContext';
import { getUnitConvertedUnit } from '@/Helpers';
import { notificationServices } from '@/Services';
import {
	createExcelProductMap,
	createStoreMapping,
	findItemNameColumn,
	findSkuCodeColumn,
	parseTSVData,
	updateTableRowsWithExcelData
} from './clipboardUtils';
import GlobalSearch from '@/Components/globalSearch/search';
import moment from 'moment';
import { buildRowsFromMasterSheet, isMasterSheet, parseMasterSheet } from './utils/masterSheetPaste';
import { getRowItemId, isCountItem } from './utils/countUnits';

const Functional = (props) => {
	const { stores = [], functionality = [], mode = {}, currentStep, setConfirmationModal = () => { }, editMode = false } = props;
	const productList = useSelector(
		(state) => state?.form?.ALL_PRODUCTS?.data?.LIST
	);
	const { state, dispatch, tableData, lists, setTableData, changeQtyRef, getProductData, viewMode, setSearchQuery, originalRowsRef, productInfoById, qtyByWeight, setQtyByWeight } = useMultiplePo();
	// "Enter by weight" is offered only when the table has at least one count item.
	const hasCountItem = (tableData?.rows ?? []).some((row) => isCountItem(productInfoById?.[getRowItemId(row)]));
	const [isProcessingClipboard, setIsProcessingClipboard] = useState(false);
	const pendingRowsRef = useRef(null);
	// A master-sheet paste waiting for the reload its delivery date change triggers.
	const pendingMasterPasteRef = useRef(null);
	const pasteStatsRef = useRef(null);

	const SelectedProductIds = useMemo(() => {
		const selectedProducts = [];
		tableData?.rows?.forEach((row) => {
			Object.values(row).forEach((item) => {
				if (item?.itemId && !selectedProducts.includes(item?.itemId)) {
					selectedProducts.push(item?.itemId);
				}
			});
		});
		return selectedProducts;
	}, [tableData?.rows]);

	const handleAddItems = (selectedProductIds) => {
		const allProducts = getProductData;
		const currentRows = [...tableData?.rows];
		let updatedRows = [];
		const rowMap = {};
		if (selectedProductIds.length < currentRows.length) {
			updatedRows = currentRows.filter((row) => {
				const itemId = Object.values(row).find(
					(cell) => cell && typeof cell === 'object' && cell.itemId
				)?.itemId;
				return selectedProductIds.includes(itemId);
			});
		} else {
			currentRows.forEach((row, idx) => {
				const itemId = Object.values(row).find(
					(cell) => cell && typeof cell === 'object' && cell.itemId
				)?.itemId;
				if (itemId) rowMap[itemId] = idx;
			});

			selectedProductIds.forEach((itemId) => {
				if (!(itemId in rowMap)) {
					const product = allProducts.find((p) => p.value === itemId);
					if (product) {
						const newRow = {
							Actions: {
								rowIndex: currentRows.length,
							},
							'S.No': '',
							'Item Name': product.viewOnlyLabel,
							itemId: product.value,
							SkuCode: product?.clientVariantId,
							originalUnit: product?.unit,
							Total: '',
							...stores?.reduce((acc, store) => {
								acc[`${store?.id}`] = {
									qty: '',
									unit: getUnitConvertedUnit(
										null,
										product?.unit,
										true
									),
									storeId: store?.id,
									itemId: product?.value,
									productId: product?.productId,
								};
								return acc;
							}, {}),
						};
						currentRows.push(newRow);
					}
				}
			});
			// Update rowIndex for all rows after adding new items
			updatedRows = currentRows.map((row, index) => ({
				...row,
				Actions: {
					rowIndex: index,
				},
			}));
		}
		setTableData({
			...tableData, rows: updatedRows,
		});
	};

	const applyRows = (rows, stats) => {
		if (mode?.modeName === 'buyerOrder' && stats?.storeMapping) {
			const matchedLocationIds = Object.values(stats.storeMapping)
				.map((h) => {
					if (typeof h === 'string' && h.includes('_')) {
						return Number(h.split('_')[1]);
					}
					return Number(h);
				})
				.filter(Boolean);

			if (matchedLocationIds.length > 0) {
				const currentBuyerIds = (state?.buyer || []).map((id) => Number(id));
				const newBuyerIds = Array.from(
					new Set([...currentBuyerIds, ...matchedLocationIds])
				);

				if (newBuyerIds.length !== currentBuyerIds.length) {
					dispatch({
						type: 'SET_STATE',
						payload: { buyer: newBuyerIds },
					});
				}
			}
		}

		setTableData({
			...tableData, rows,
		});

		notificationServices.generateNotification({
			type: 'success',
			message: `Successfully processed clipboard data. Matched ${stats.matchedCount} items, updated ${stats.updatedCount} quantities${stats.addedCount > 0
				? `, and added ${stats.addedCount} new products`
				: ''
				}.`,
			config: {
				duration: 5000,
			},
		});
	};

	const closeModal = () => {
		setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
		pendingRowsRef.current = null;
		pasteStatsRef.current = null;
	};

	const handleConfirmFoundOnly = () => {
		if (!pendingRowsRef.current) return;
		const foundRows = pendingRowsRef.current
			.filter((row) => !row._isNotFound)
			.map((row, index) => ({
				...row,
				'S.No': index + 1,
				Actions: { rowIndex: index },
			}));
		applyRows(foundRows, pasteStatsRef.current);
		closeModal();
	};

	const handleConfirmAll = () => {
		if (!pendingRowsRef.current) return;
		applyRows(pendingRowsRef.current, pasteStatsRef.current);
		closeModal();
	};

	// Shared end of both paste paths: decimal check, not-found confirmation, apply.
	const finishPaste = ({ updatedRows, matchedCount, updatedCount, addedCount, storeMapping }) => {
		// Add Actions column to updatedRows
		const rowsWithActions = updatedRows.map((row, index) => ({
			...row,
			Actions: {
				rowIndex: index,
			},
		}));

		let hasDecimalError = false;
		rowsWithActions.forEach((row) => {
			Object.entries(row).forEach(([key, storeData]) => {
				if (storeData && typeof storeData === 'object' && storeData.qty !== undefined && storeData.unit) {
					if (['pc', 'count'].includes(storeData.unit?.toLowerCase()) && storeData.qty?.toString()?.includes('.')) {
						if (!hasDecimalError) {
							notificationServices.generateNotification({
								type: 'error',
								message: `Decimal values are not allowed for ${storeData.unit}. Product: ${row['Item Name']}`,
							});
							hasDecimalError = true;
						}
					}
				}
			});
		});

		if (!hasDecimalError) {
			// Check for not-found items
			const notFound = rowsWithActions.filter((row) => row._isNotFound);
			if (notFound.length > 0) {
				// Store pending data in refs and show confirmation modal via parent
				pendingRowsRef.current = rowsWithActions;
				pasteStatsRef.current = { matchedCount, updatedCount, addedCount, storeMapping };
				const notFoundNames = notFound.map((row) => row['Item Name'] || row._excelName);
				setConfirmationModal({
					isOpen: true,
					heading: 'Items Not Found',
					textAlign: true,
					width: 'w-[600px] max-w-2xl',
					message: (
						<div className='flex flex-col gap-2'>
							<p className='text-[13px] text-gray-600'>
								{notFoundNames.length} item{notFoundNames.length > 1 ? 's' : ''} could not be matched in our system:
							</p>
							<div className='flex flex-col gap-1.5 max-h-[200px] overflow-y-auto'>
								{notFoundNames.map((item, index) => (
									<div
										key={index}
										className='flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-100 rounded-md'
									>
										<X className='w-3 h-3 text-red-400 flex-shrink-0' />
										<span className='text-[12px] text-red-700 font-medium truncate'>
											{item}
										</span>
									</div>
								))}
							</div>
							<p className='text-[12px] text-gray-500 mt-1'>
								Would you like to proceed with only the found items, or keep all items?
							</p>
						</div>
					),
					customButton: [
						{
							label: 'Found Items Only',
							onClick: handleConfirmFoundOnly,
							className: 'bg-blue-600 hover:bg-blue-700 text-white',
						},
						{
							label: 'Keep All Items',
							onClick: handleConfirmAll,
							className: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
						},
						{
							label: 'Cancel',
							onClick: closeModal,
							className: 'text-gray-400 hover:text-gray-600',
						},
					],
				});
			} else {
				applyRows(rowsWithActions, { matchedCount, updatedCount, addedCount, storeMapping });
			}
		}
	};

	// Apply a waiting master-sheet paste once the table rows were reloaded for its date.
	useEffect(() => {
		const pending = pendingMasterPasteRef.current;
		if (!pending || tableData?.rows === pending.rowsBefore) return;
		const current = state?.expectedDeliveryDate
			? moment(state.expectedDeliveryDate).format('YYYY-MM-DD')
			: null;
		if (current !== pending.date) return;
		pendingMasterPasteRef.current = null;
		finishPaste(pending.result);
	}, [tableData?.rows, state?.expectedDeliveryDate]);

	const processClipboardData = async () => {
		changeQtyRef.current = false;
		setIsProcessingClipboard(true);

		try {
			// Read clipboard content
			const clipboardText = await navigator.clipboard.readText();

			if (!clipboardText) {
				notificationServices.generateNotification({
					type: 'warning',
					message: 'No data found in clipboard',
				});
				return;
			}

			// Kitchen master sheet (date in A1, Pax Projection / Standard Portion Size blocks per
			// shift section) - see utils/masterSheetPaste.
			if (mode?.modeName !== 'buyerOrder' && isMasterSheet(clipboardText)) {
				// The sheet's date is the delivery date. Rows are built for it right away (the
				// table's store columns do not depend on the date).
				const sheetDate = parseMasterSheet(clipboardText).date;
				const currentDate = state?.expectedDeliveryDate
					? moment(state.expectedDeliveryDate).format('YYYY-MM-DD')
					: null;
				const result = buildRowsFromMasterSheet({
					text: clipboardText,
					selectedShiftId: state?.internalShift,
					selectedDate: sheetDate ?? currentDate,
					shifts: lists?.shiftList ?? [],
					tableHeaders: tableData?.headers ?? [],
					stores,
					products: getProductData ?? [],
				});
				if (result.error) {
					notificationServices.generateNotification({ type: 'error', message: result.error });
					return;
				}
				result.notes?.forEach((note) => notificationServices.generateNotification({
					type: 'warning',
					message: note,
				}));
				if (sheetDate && sheetDate !== currentDate) {
					// Changing Expected Delivery Date reloads the table rows, which would wipe a paste
					// applied now - so set the date, and apply the rows once that reload has landed
					// (effect below), or after a few seconds if no reload comes.
					const [y, m, d] = sheetDate.split('-').map(Number);
					pendingMasterPasteRef.current = { result, date: sheetDate, rowsBefore: tableData?.rows };
					setTimeout(() => {
						if (pendingMasterPasteRef.current?.result === result) {
							pendingMasterPasteRef.current = null;
							finishPaste(result);
						}
					}, 8000);
					dispatch({ type: 'SET_STATE', payload: { expectedDeliveryDate: new Date(y, m - 1, d) } });
					notificationServices.generateNotification({
						type: 'success',
						message: `Expected Delivery Date set to ${d.toString().padStart(2, '0')}-${m.toString().padStart(2, '0')}-${y} from the sheet`,
					});
					return;
				}
				finishPaste(result);
				return;
			}

			// Parse TSV data
			const parsedData = parseTSVData(clipboardText);

			if (!parsedData) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Invalid data format. Please copy excel values.',
				});
				return;
			}

			const { headers: clipboardHeaders, rows: clipboardRows } =
				parsedData;
			// Find Item Name column
			const itemNameColumn = findItemNameColumn(clipboardHeaders);
			if (!itemNameColumn) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Item Name column not found in clipboard data',
				});
				return;
			}
			// Detect SKU code column
			const skuCodeColumn = findSkuCodeColumn(clipboardHeaders);

			let mappingHeaders = [...tableData.headers];
			const fullLocationHeaders = [];
			if (mode?.modeName === 'buyerOrder' && lists?.buyerList?.length > 0) {
				lists.buyerList.forEach((buyer) => {
					buyer?.vendor_locations?.forEach((location) => {
						fullLocationHeaders.push({
							name: location?.name,
							storeName: location?.name,
							id: `${buyer.id}_${location.id}`,
							groupName: buyer?.label || buyer?.name,
						});
					});
				});
				mappingHeaders = [
					...(mode?.tableColumns || []),
					...fullLocationHeaders,
					{ name: 'Total', id: 'Total' },
					{ name: 'Actions', id: 'Actions' },
				];
			}

			const storeMapping = createStoreMapping(
				clipboardHeaders,
				mappingHeaders,
				itemNameColumn,
				skuCodeColumn
			);
			// Create excel product map and update table data
			const excelProductMap = createExcelProductMap(
				clipboardRows,
				itemNameColumn
			);
			const { updatedRows, matchedCount, updatedCount, addedCount } =
				updateTableRowsWithExcelData(
					tableData.rows,
					clipboardRows,
					excelProductMap,
					storeMapping,
					itemNameColumn,
					getProductData,
					mode?.modeName === 'buyerOrder' ? fullLocationHeaders : stores,
					skuCodeColumn
				);
			finishPaste({ updatedRows, matchedCount, updatedCount, addedCount, storeMapping });
		} catch (error) {
			console.error('Error processing clipboard:', error);
			notificationServices.generateNotification({
				type: 'error',
				message:
					'Error reading clipboard. Please ensure you have copied the data correctly.',
			});
		} finally {
			setIsProcessingClipboard(false);
		}
	};

	const handleSearch = (value) => {
		setSearchQuery(value);
		if (value && !originalRowsRef.current) {
			originalRowsRef.current = [...tableData?.rows];
		}

		if (!value) {
			if (originalRowsRef.current) {
				setTableData({
					...tableData,
					rows: originalRowsRef.current,
				});
				originalRowsRef.current = null;
			}
			return;
		}

		const filteredRows = originalRowsRef.current.filter((row) =>
			row[mode?.searchColumn]?.toLowerCase?.()?.includes(value?.toLowerCase?.())
		);
		setTableData({
			...tableData,
			rows: filteredRows,
		});
	};


	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center justify-between'>
				{functionality?.includes('search') && (!currentStep || currentStep === 1 || currentStep === 2 || (editMode && currentStep === 4)) && (
					<GlobalSearch
						placeholderText='Search Products...'
						searchDropDown={false}
						setSearchValue={handleSearch}
					/>
				)}
				<div className='flex items-center gap-2 flex-1'>
					{functionality?.includes('addProducts') && (
						<>
							<p className='text-[14px] font-medium whitespace-nowrap'>
								Add Products:
							</p>
							<CheckPicker
								data={getProductData}
								placeholder='Select Products...'
								value={SelectedProductIds}
								onChange={handleAddItems}
								virtualized
								menuStyle={{
									zIndex: 99999,
								}}
								className='w-[500px]'
								cleanable={false}
								disabled={viewMode}
							/>
						</>
					)}
					{functionality?.includes('paste') && (
						<Button
							variant='outline'
							size='sm'
							onClick={processClipboardData}
							disabled={
								isProcessingClipboard ||
								tableData?.headers?.length === 0 || viewMode
							}
							className='flex items-center gap-2 px-3 py-2 border border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-colors'
							title='Paste data from clipboard (TSV format)'
						>
							<ClipboardPaste className='w-4 h-4' />
							{isProcessingClipboard
								? 'Processing...'
								: 'Paste Data'}
						</Button>
					)}
					{hasCountItem && (
						<div
							className='flex items-center gap-2 whitespace-nowrap'
							title="Weight: type quantities of count items in kg; they are saved as a count using each item's minimum weight"
						>
							<span className='text-[13px] font-medium text-gray-700'>Order by</span>
							<div className='flex overflow-hidden rounded-md border border-gray-300'>
								{[
									{ key: 'count', label: 'Count', byWeight: false },
									{ key: 'weight', label: 'Weight', byWeight: true },
								].map((opt) => {
									const active = !!qtyByWeight === opt.byWeight;
									return (
										<button
											key={opt.key}
											type='button'
											disabled={viewMode}
											onClick={() => setQtyByWeight(opt.byWeight)}
											className={`px-3 py-1 text-[13px] font-medium transition-colors ${active
												? 'bg-blue-600 text-white'
												: 'bg-white text-gray-700 hover:bg-gray-50'}`}
										>
											{opt.label}
										</button>
									);
								})}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Functional;
